import React, { useCallback, useEffect, useRef, useState } from "react"
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import API from "../../common/API"

interface BatchStats {
  processed?: number
  created?: number
  skipped?: number
  updated?: number
  migrated?: number
  padep?: number
  pip?: number
  matched?: number
  ambiguous?: number
  notMatched?: number
  preAssigned?: number
  errors: number
}

interface ServiceStatus {
  name: string
  description: string
  lastRunAt: string | null
  lastError: string | null
  pending?: number
  running?: number
  lastBatch: BatchStats
}

interface HistoryRecord {
  id: string
  processName: string
  triggerType: string
  status: string
  userId?: string | null
  userName?: string | null
  organizationId?: string | null
  startedAt: string
  completedAt?: string | null
  durationMs?: number | null
  summary?: string | null
  error?: string | null
  metrics?: BatchStats | null
  hostName?: string | null
}

const AdminSystemMonitorComponent = () => {
  const [services, setServices] = useState<ServiceStatus[]>([])
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState<Record<string, boolean>>({})
  const [emailTo, setEmailTo] = useState("")
  const [emailSubject, setEmailSubject] = useState("Test email desde Dataworker")
  const [emailBody, setEmailBody] = useState("Este es un correo de prueba enviado desde el panel de administración.")
  const [sendingEmail, setSendingEmail] = useState(false)
  const [history, setHistory] = useState<HistoryRecord[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyPage, setHistoryPage] = useState(0)
  const [historyTotalPages, setHistoryTotalPages] = useState(1)
  const [historyProcessName, setHistoryProcessName] = useState("")
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    try {
      const res = await API.getAction("SystemMonitor/Status")
      if (res.data.status === "ok") setServices(res.data.response.services)
    } catch { /* silent */ }
    finally { setLoading(false) }
  }, [])

  const fetchHistory = useCallback(async (page: number, processName: string) => {
    setHistoryLoading(true)
    try {
      const qs = new URLSearchParams({
        Page: String(page),
        Quantity: "10",
      })
      if (processName) qs.set("ProcessName", processName)

      const res = await API.getAction(`SystemMonitor/History?${qs.toString()}`)
      if (res.data.status === "ok") {
        setHistory(res.data.response)
        setHistoryTotalPages(res.data.quantityOfPages || 1)
      }
    } catch {
      toast.error("No se pudo cargar el historial del monitor")
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    fetchHistory(0, "")
    intervalRef.current = setInterval(fetchStatus, 10000)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [fetchStatus, fetchHistory])

  const runService = async (endpoint: string, label: string) => {
    setRunning(r => ({ ...r, [endpoint]: true }))
    try {
      const res = await API.postAction(`SystemMonitor/${endpoint}`, {})
      if (res.data.status === "ok") {
        toast.success(`${label}: ${JSON.stringify(res.data.response)}`)
        await fetchStatus()
        await fetchHistory(0, historyProcessName)
      } else {
        toast.error(`Error: ${res.data.response}`)
      }
    } catch (ex: any) {
      toast.error(ex.message)
    } finally {
      setRunning(r => ({ ...r, [endpoint]: false }))
    }
  }

  const sendTestEmail = async () => {
    if (!emailTo.trim()) { toast.error("Ingresa un destinatario"); return }
    setSendingEmail(true)
    try {
      const res = await API.postAction("SystemMonitor/TestEmail", { to: emailTo, subject: emailSubject, body: emailBody })
      if (res.data.status === "ok") toast.success(res.data.response)
      else toast.error(res.data.response)
    } catch (ex: any) {
      toast.error(ex.message)
    } finally {
      setSendingEmail(false)
    }
  }

  const formatDate = (d: string | null) => {
    if (!d) return <span className="text-muted">Nunca</span>
    return new Date(d).toLocaleString("es-DO")
  }

  const formatDuration = (durationMs?: number | null) => {
    if (!durationMs && durationMs !== 0) return "—"
    if (durationMs < 1000) return `${durationMs} ms`
    return `${(durationMs / 1000).toFixed(1)} s`
  }

  const renderHistoryStatus = (status: string) => {
    const normalized = status.toLowerCase()
    if (normalized === "completed") return <Badge bg="success">Completado</Badge>
    if (normalized === "failed") return <Badge bg="danger">Falló</Badge>
    if (normalized === "nowork") return <Badge bg="secondary">Sin trabajo</Badge>
    return <Badge bg="dark">{status}</Badge>
  }

  const serviceEndpoint: Record<string, string> = {
    FichadosAutoSync: "RunFichadosAutoSync",
    PartesCedulaMatch: "RunPartesCedulaMatch",
    CentrosAutoClassification: "RunCentrosAutoClassification",
    JudicialCaseSyncQueue: "RunJudicialCaseSyncQueue",
  }

  const renderBatch = (b: BatchStats) => (
    <div className="d-flex flex-wrap gap-2 mt-2">
      {b.processed !== undefined && <Badge bg="secondary">Procesados: {b.processed}</Badge>}
      {b.created !== undefined && <Badge bg="success">Creados: {b.created}</Badge>}
      {b.skipped !== undefined && <Badge bg="light" text="dark">Saltados: {b.skipped}</Badge>}
      {b.updated !== undefined && <Badge bg="primary">Actualizados: {b.updated}</Badge>}
      {b.migrated !== undefined && <Badge bg="dark">Migrados: {b.migrated}</Badge>}
      {b.padep !== undefined && <Badge bg="danger">PADEP: {b.padep}</Badge>}
      {b.pip !== undefined && <Badge bg="warning" text="dark">PIP: {b.pip}</Badge>}
      {b.matched !== undefined && <Badge bg="success">Matched: {b.matched}</Badge>}
      {b.ambiguous !== undefined && <Badge bg="warning" text="dark">Ambiguos: {b.ambiguous}</Badge>}
      {b.notMatched !== undefined && <Badge bg="danger">No match: {b.notMatched}</Badge>}
      {b.preAssigned !== undefined && <Badge bg="info">Pre-asig: {b.preAssigned}</Badge>}
      {b.errors > 0 && <Badge bg="danger">Errores: {b.errors}</Badge>}
    </div>
  )

  return (
    <div className="p-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0"><i className="fas fa-server me-2" />Monitor del Sistema</h5>
        <Button variant="outline-secondary" size="sm" onClick={async () => { await fetchStatus(); await fetchHistory(historyPage, historyProcessName) }} disabled={loading}>
          <i className="fas fa-sync-alt me-1" />Actualizar
        </Button>
      </div>

      {/* Background Services */}
      <h6 className="text-muted mb-2">Procesos en Background</h6>
      <Row className="g-3 mb-4">
        {services.map(svc => {
          const ep = serviceEndpoint[svc.name]
          const isRunning = running[ep]
          const hasError = !!svc.lastError
          return (
            <Col md={6} key={svc.name}>
              <Card className={`h-100 ${hasError ? "border-danger" : "border-0 shadow-sm"}`}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <strong>{svc.name}</strong>
                      <div className="text-muted small">{svc.description}</div>
                    </div>
                    <Badge bg={svc.lastRunAt ? (hasError ? "danger" : "success") : "secondary"}>
                      {svc.lastRunAt ? (hasError ? "Error" : "OK") : "Sin correr"}
                    </Badge>
                  </div>

                  <Table size="sm" borderless className="mb-2 small">
                    <tbody>
                      <tr>
                        <td className="text-muted ps-0">Último run</td>
                        <td>{formatDate(svc.lastRunAt)}</td>
                      </tr>
                    </tbody>
                  </Table>

                  {renderBatch(svc.lastBatch)}
                  {(svc.pending !== undefined || svc.running !== undefined) && (
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {svc.pending !== undefined && <Badge bg="secondary">Pendientes: {svc.pending}</Badge>}
                      {svc.running !== undefined && <Badge bg="info">Corriendo: {svc.running}</Badge>}
                    </div>
                  )}

                  {hasError && (
                    <div className="alert alert-danger py-1 px-2 small mt-2 mb-0">
                      <i className="fas fa-exclamation-circle me-1" />
                      {svc.lastError}
                    </div>
                  )}

                  {ep && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="mt-3 w-100"
                      onClick={() => runService(ep, svc.name)}
                      disabled={isRunning}
                    >
                      {isRunning
                        ? <><Spinner size="sm" className="me-1" />Ejecutando...</>
                        : <><i className="fas fa-play me-1" />Ejecutar ahora</>}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>
          )
        })}
      </Row>

      <h6 className="text-muted mb-2">Historial auditable</h6>
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-2 align-items-end mb-3">
            <Col md={4}>
              <Form.Label className="small fw-semibold">Proceso</Form.Label>
              <Form.Select
                size="sm"
                value={historyProcessName}
                onChange={async e => {
                  const next = e.target.value
                  setHistoryProcessName(next)
                  setHistoryPage(0)
                  await fetchHistory(0, next)
                }}
              >
                <option value="">Todos</option>
                {services.map(svc => (
                  <option key={svc.name} value={svc.name}>{svc.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => fetchHistory(historyPage, historyProcessName)}
                disabled={historyLoading}
              >
                {historyLoading ? <><Spinner size="sm" className="me-1" />Cargando...</> : <><i className="fas fa-history me-1" />Recargar historial</>}
              </Button>
            </Col>
          </Row>

          <div className="table-responsive">
            <Table hover size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Inicio</th>
                  <th>Proceso</th>
                  <th>Trigger</th>
                  <th>Estado</th>
                  <th>Usuario</th>
                  <th>Duración</th>
                  <th>Resumen</th>
                  <th>Métricas</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      <Spinner size="sm" className="me-2" />Cargando historial...
                    </td>
                  </tr>
                ) : history.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      No hay corridas auditadas para mostrar.
                    </td>
                  </tr>
                ) : history.map(item => (
                  <tr key={item.id}>
                    <td>
                      <div>{formatDate(item.startedAt)}</div>
                      <div className="text-muted small">{item.hostName || "Servidor"}</div>
                    </td>
                    <td><strong>{item.processName}</strong></td>
                    <td>{item.triggerType === "Manual" ? <Badge bg="primary">Manual</Badge> : <Badge bg="dark">Automático</Badge>}</td>
                    <td>{renderHistoryStatus(item.status)}</td>
                    <td>{item.userName || <span className="text-muted">Sistema</span>}</td>
                    <td>{formatDuration(item.durationMs)}</td>
                    <td>
                      <div>{item.summary || "—"}</div>
                      {item.error && <div className="text-danger small mt-1">{item.error}</div>}
                    </td>
                    <td>{item.metrics ? renderBatch(item.metrics) : <span className="text-muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={historyPage === 0 || historyLoading}
              onClick={async () => {
                const next = Math.max(0, historyPage - 1)
                setHistoryPage(next)
                await fetchHistory(next, historyProcessName)
              }}
            >
              ‹ Anterior
            </Button>
            <small className="text-muted">Página {historyTotalPages === 0 ? 0 : historyPage + 1} de {historyTotalPages}</small>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={historyLoading || historyPage >= historyTotalPages - 1}
              onClick={async () => {
                const next = historyPage + 1
                setHistoryPage(next)
                await fetchHistory(next, historyProcessName)
              }}
            >
              Siguiente ›
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Email Test */}
      <h6 className="text-muted mb-2">Test de Correo</h6>
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Row className="g-2">
            <Col md={4}>
              <Form.Label className="small fw-semibold">Destinatario</Form.Label>
              <Form.Control
                size="sm"
                type="email"
                placeholder="correo@ejemplo.com"
                value={emailTo}
                onChange={e => setEmailTo(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-semibold">Asunto</Form.Label>
              <Form.Control
                size="sm"
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
              />
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button
                variant="outline-primary"
                size="sm"
                className="w-100"
                onClick={sendTestEmail}
                disabled={sendingEmail}
              >
                {sendingEmail
                  ? <><Spinner size="sm" className="me-1" />Enviando...</>
                  : <><i className="fas fa-paper-plane me-1" />Enviar Test</>}
              </Button>
            </Col>
            <Col md={12}>
              <Form.Label className="small fw-semibold">Cuerpo</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={emailBody}
                onChange={e => setEmailBody(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  )
}

export default AdminSystemMonitorComponent
